import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Search, User, CreditCard, Activity, Shield, ArrowRight, RefreshCw, UserCheck } from "lucide-react";
import api from "@/lib/api";

/**
 * AdminUserLookup — /admin/users. Search by email, get the full 360°
 * view of that account (subscription, trial, PayTabs history, Plaid
 * connections, recent analytics events, extend audit).
 *
 * Read-only surface. Mutations (extend trial, revoke) live on
 * /admin/trials — this page links out.
 */
export default function AdminUserLookup() {
  const [secret, setSecret] = useState("");
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookup = async () => {
    if (!secret || !email.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data } = await api.get(`/auth/admin/user-lookup?email=${encodeURIComponent(email.trim())}`, {
        headers: { "X-Admin-Secret": secret },
      });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Lookup failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") lookup(); };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
          Admin · User Lookup
        </div>
        <h1 className="text-3xl font-heading font-medium text-[var(--text-primary)]">
          Search a user by email
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
          Single-page 360° view: subscription tier, trial state, PayTabs
          payment history, Plaid connections, admin extend log, and
          recent analytics events.
        </p>
      </div>

      <div className="card-flat p-5 mb-6 flex flex-wrap items-center gap-3">
        <Shield size={16} className="text-[var(--text-secondary)]" />
        <input
          type="password"
          className="field-input flex-1 min-w-[200px]"
          placeholder="Paste ADMIN_SECRET"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          data-testid="admin-user-secret"
        />
        <Search size={16} className="text-[var(--text-secondary)]" />
        <input
          type="email"
          className="field-input flex-1 min-w-[240px]"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKey}
          data-testid="admin-user-email"
        />
        <button
          className="btn-primary"
          onClick={lookup}
          disabled={loading || !secret || !email.trim()}
          data-testid="admin-user-search"
        >
          {loading ? <RefreshCw size={15} className="animate-spin" /> : <Search size={15} />}
          {loading ? "Searching…" : "Lookup"}
        </button>
      </div>

      {error && (
        <div className="card-flat p-4 mb-6 border-[var(--accent-danger)] text-sm text-[var(--accent-danger)]" data-testid="admin-user-error">
          {error}
        </div>
      )}

      {data && <UserPanel data={data} />}

      <div className="mt-8 flex flex-wrap gap-3 text-xs">
        <Link to="/admin/metrics" className="btn-ghost" data-testid="admin-user-back-metrics">
          Back to metrics <ArrowRight size={12} />
        </Link>
        <Link to="/admin/trials" className="btn-ghost" data-testid="admin-user-goto-trials">
          Extend a trial <ArrowRight size={12} />
        </Link>
        <Link to="/admin/waitlist" className="btn-ghost" data-testid="admin-user-goto-waitlist">
          Payment waitlist <ArrowRight size={12} />
        </Link>
        <Link to="/admin/audit" className="btn-ghost" data-testid="admin-user-goto-audit">
          Audit log <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

// Column definitions hoisted to module scope so they're not re-allocated
// on every render (iter-61 audit fix — inline arrays trigger unnecessary
// child re-renders in the SectionCard).
const PAYTABS_COLS = [
  { key: "received_at", label: "When", render: (r) => fmtDateTime(r.received_at) },
  { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
  { key: "amount", label: "Amount", render: (r) => r.amount ? `${r.amount} ${r.currency || ""}` : "—" },
  { key: "tran_ref", label: "Tran ref", render: (r) => <span className="font-mono text-[11px]">{r.tran_ref || "—"}</span> },
];

const EXTEND_COLS = [
  { key: "at", label: "When", render: (r) => fmtDateTime(r.at) },
  { key: "days", label: "Days added", render: (r) => `+${r.days || 0}` },
  { key: "previous_trial_ends_at", label: "Was", render: (r) => fmtDateShort(r.previous_trial_ends_at) },
  { key: "new_trial_ends_at", label: "Now", render: (r) => fmtDateShort(r.new_trial_ends_at) },
];

const EVENT_COLS = [
  { key: "at", label: "When", render: (r) => fmtDateTime(r.at) },
  { key: "event", label: "Event", render: (r) => <span className="font-mono text-[11px]">{r.event}</span> },
  { key: "source", label: "Source", render: (r) => r.source || "—" },
];

function UserPanel({ data }) {
  const u = data.user;
  const tierTone = u.subscription_tier === "pro" ? "text-[var(--accent-success)]" :
                   u.trial_is_expired ? "text-[var(--accent-danger)]" :
                   "text-[var(--text-secondary)]";
  return (
    <div className="space-y-6" data-testid="admin-user-panel">
      {/* Header card */}
      <div className="card-flat p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-[var(--bg-accent)]">
              <User size={22} className="text-[var(--accent-primary)]" />
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow">User</div>
              <div className="text-lg font-medium truncate">{u.name || "—"}</div>
              <div className="text-sm text-[var(--text-secondary)] truncate">{u.email}</div>
              <div className="text-[11px] font-mono text-[var(--text-muted)] mt-1">{u.user_id}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Tier</div>
            <div className={`text-lg font-medium ${tierTone}`} data-testid="admin-user-tier">
              {u.subscription_tier?.toUpperCase() || "FREE"}
            </div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {u.currency} · {u.language || "en"}
            </div>
            <ImpersonateButton user={u} />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-[var(--border-default)]">
          <Field label="Joined" value={fmtDateShort(u.created_at)} />
          <Field label="Trial ends" value={fmtDateShort(u.trial_ends_at)}
                 sub={u.trial_days_remaining != null ? `${u.trial_days_remaining} day${u.trial_days_remaining === 1 ? "" : "s"} remaining` : null}
                 tone={u.trial_is_expired ? "danger" : null} />
          <Field label="Subscription plan" value={u.subscription_plan || "—"}
                 sub={u.subscription_period_end ? `until ${fmtDateShort(u.subscription_period_end)}` : null} />
          <Field label="Subscription status" value={u.subscription_status || "—"}
                 sub={u.cancel_at_period_end ? "cancel at period end" : null} />
          <Field label="Wall opt-in" value={u.wall_opt_in ? "Yes" : "No"} />
          <Field label="Plaid connections" value={String(data.connections.plaid_item_count)} />
        </div>
      </div>

      {/* PayTabs history */}
      <SectionCard
        icon={<CreditCard size={16} />}
        title="PayTabs payment history"
        empty="No PayTabs events for this user."
        rows={data.paytabs_events}
        rowKey={(r) => `${r.tran_ref || "no-ref"}:${r.received_at || ""}`}
        columns={PAYTABS_COLS}
        testid="admin-user-paytabs"
      />

      {/* Extend audit */}
      <SectionCard
        icon={<Shield size={16} />}
        title="Admin extend history"
        empty="No admin trial extends for this user."
        rows={data.recent_extends}
        rowKey={(r) => `${r.at || "unknown"}:${r.days || 0}`}
        columns={EXTEND_COLS}
        testid="admin-user-extends"
      />

      {/* Recent analytics events */}
      <SectionCard
        icon={<Activity size={16} />}
        title="Recent activity"
        empty="No recent analytics events."
        rows={data.recent_events}
        rowKey={(r) => `${r.at || "unknown"}:${r.event || "?"}`}
        columns={EVENT_COLS}
        testid="admin-user-events"
      />
    </div>
  );
}

function Field({ label, value, sub, tone }) {
  const toneCls = tone === "danger" ? "text-[var(--accent-danger)]" : "";
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`text-sm font-medium mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionCard({ icon, title, rows, columns, empty, testid, rowKey }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[var(--text-secondary)]">{icon}</span>
        <div className="label-eyebrow">{title}</div>
      </div>
      {!rows || rows.length === 0 ? (
        <div className="text-sm text-[var(--text-muted)]">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)] border-b border-[var(--border-default)]">
                {columns.map(c => <th key={c.key} className="py-2 pr-3 font-medium">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                // Stable natural key from the row where possible; fall back
                // to index only when the caller didn't supply a rowKey (kept
                // for API compatibility, but every current caller supplies one).
                const key = rowKey ? rowKey(r) : `row-${i}`;
                return (
                  <tr key={key} className="border-b border-[var(--border-default)] last:border-0">
                    {columns.map(c => (
                      <td key={c.key} className="py-2 pr-3 align-top text-[var(--text-primary)]">
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    A: { label: "Authorised", cls: "bg-[var(--accent-success)]/15 text-[var(--accent-success)]" },
    D: { label: "Declined", cls: "bg-[var(--accent-danger)]/15 text-[var(--accent-danger)]" },
    E: { label: "Error", cls: "bg-[var(--accent-danger)]/15 text-[var(--accent-danger)]" },
    V: { label: "Voided", cls: "bg-[var(--text-muted)]/15 text-[var(--text-muted)]" },
  };
  const b = map[status] || { label: status || "—", cls: "bg-[var(--text-muted)]/15 text-[var(--text-muted)]" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${b.cls}`}>{b.label}</span>;
}

function fmtDateShort(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return v; }
}
function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return v; }
}

/**
 * Impersonate button — hits POST /auth/admin/impersonate/{user_id},
 * stores the returned session_token in the shared impersonation store,
 * and hard-reloads into /dashboard so the whole app reboots inside
 * the target user's context.
 */
function ImpersonateButton({ user }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    // Pull the admin secret from the page's own input — same pattern
    // used by the metrics dashboard so we never touch localStorage.
    const secretInput = document.querySelector('[data-testid="admin-metrics-secret"], [data-testid="admin-user-secret"]');
    const secret = secretInput?.value?.trim();
    if (!secret) {
      toast.error("Admin secret required (top of page)");
      return;
    }
    if (!confirm(`Sign in as ${user.email}? You will see their exact view for 2h.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/admin/impersonate/${user.user_id}`, null, {
        headers: { "X-Admin-Secret": secret },
      });
      // Stash the impersonation token + metadata so AuthContext picks it
      // up on the next page load and mounts the banner.
      const mod = await import("@/lib/impersonation");
      mod.startImpersonation({
        session_token: data.session_token,
        target: data.target,
        expires_at: data.expires_at,
      });
      // Full page reload — clears every context, remounts with the
      // impersonation session cookie carrying the new user's identity.
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
      setBusy(false);
    }
  };
  return (
    <button
      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors"
      style={{
        borderColor: "#E09E50",
        color: "#E09E50",
        background: "rgba(224,158,80,0.08)",
      }}
      onClick={start}
      disabled={busy}
      data-testid="admin-impersonate-btn"
      title="Log in as this user (2h scoped session)"
    >
      <UserCheck size={12} />
      {busy ? "Signing in…" : "Sign in as this user"}
    </button>
  );
}
